import { avoidFlatteningKeys, FALLBACK_TAG_CONFIG, VIDEO_PLAYER_SIZES } from '../core/constants';
import { initialAdUnitAndVastDetailsObj, initialUnsyncedDfpAdUnitObj } from './initialConfigs';
import { GAMIDS, VAST_TAG_CONSTANTS, SYMBOLS, URLENCODED } from '../core/constants';
import { getDfpNetworkConfig } from '../api/dfpNetworkConfig';
import { updateSite, createSite } from '../api/sites';
const jsondiffpatch = require('jsondiffpatch').create();

const { KEYS, VALUES } = VAST_TAG_CONSTANTS;
const { QUESTION_MARK, EQUAL, AMPERSAND } = SYMBOLS;
const { ENCODED_PIPE, ENCODED_AMPERSAND, ENCODED_EQUAL } = URLENCODED;

const { TAG_CONTENT, TAG_TYPE, TYPES } = FALLBACK_TAG_CONFIG;
const { SCRIPT, HTML } = TYPES;

export const flattenObject = (obj, excludedKeys = avoidFlatteningKeys) => {
	const flattened = {};

	function flatten(obj, prefix = '') {
		Object.entries(obj).forEach(([key, value]) => {
			const propName = prefix ? `${prefix}.${key}` : key;

			if (excludedKeys.includes(propName) || Array.isArray(value)) {
				flattened[propName] = structuredClone(value); // Exclude from flattening, keep as nested object or array
			} else if (typeof value === 'object' && value !== null) {
				flatten(value, propName); // Recursively flatten nested objects
			} else {
				flattened[propName] = value; // Flatten other values
			}
		});
	}

	flatten(obj);

	return flattened;
};

export const generateConfig = (flattenedObj) => {
	const regenerated = {};

	for (const key in flattenedObj) {
		const value = flattenedObj[key];
		const keys = key.split('.');
		let currentObj = regenerated;

		for (let i = 0; i < keys.length - 1; i++) {
			const currentKey = keys[i];
			if (!currentObj[currentKey] || typeof currentObj[currentKey] !== 'object') {
				currentObj[currentKey] = {};
			}
			currentObj = currentObj[currentKey];
		}

		const lastKey = keys[keys.length - 1];
		if (typeof value === 'string' && value.startsWith('{') && value.endsWith('}')) {
			currentObj[lastKey] = JSON.parse(value);
		} else {
			currentObj[lastKey] = value;
		}
	}

	return regenerated;
};

export const isJsonString = (str) => {
	try {
		const parsedValue = JSON.parse(str);
		if (typeof parsedValue !== 'object' || parsedValue === null) {
			return false;
		}
	} catch (e) {
		return false;
	}
	return true;
};

export const getAspectRatio = (width, height) => {
	try {
		const calculatedAspectRatio = parseFloat((Number(width) / Number(height)).toFixed(2));

		const aspectRatio16By9 = parseFloat((16 / 9).toFixed(2));
		const aspectRatio4By3 = parseFloat((4 / 3).toFixed(2));

		if (calculatedAspectRatio === aspectRatio16By9)
			return {
				aspectRatio: '16/9',
				valid: true
			};
		if (calculatedAspectRatio === aspectRatio4By3)
			return {
				aspectRatio: '4/3',
				valid: true
			};
		return {
			aspectRatio: '',
			valid: false
		};
	} catch (err) {
		console.log(err);
		return {
			aspectRatio: '',
			valid: false
		};
	}
};

export const generateObjectKeyFromArray = (arr) => {
	const objectKey = arr.reduce((acc, cur) => {
		if (acc === '') {
			acc = acc + cur;
			return acc;
		}
		acc = acc + '.' + cur;
		return acc;
	}, '');

	return objectKey;
};

// TODO: Add this Logic to Server End as well.
export const voidOfSpecialCharacters = (str) => {
	const regex = /^[a-zA-Z0-9_.-]+$/;
	return regex.test(str);
};

/**
 * The function takes a string message and returns its SHA-256 hash in hexadecimal format using the Web
 * Crypto API.
 * @param {String} message -  String that you want to convert to a SHA-256 hash.
 * @returns a Promise that resolves to a string representing the SHA-256 hash of the input message. If
 * the browser does not support the Web Crypto API, the function returns null.
 */
export async function stringToSha256(message) {
	try {
		if (!window.crypto) return null;
		let hashHex = stringToHash(message, 'SHA-256');
		return hashHex;
	} catch (err) {
		return null;
	}
}

/**
 * The function takes a message and an optional algorithm as input, and returns a hash of the message
 * using the specified algorithm.
 * @param {String} message - The message that needs to be hashed.
 * @param {String} [algo=SHA-256] - The hashing algorithm to be used. The default value is SHA-256.
 * @returns a Promise that resolves to a string representing the hash value of the input message using
 * the specified algorithm (default is SHA-256). If there is an error, the function returns null.
 */
export async function stringToHash(message, algo = 'SHA-256') {
	try {
		if (!window.crypto) return null;
		const msgUint8 = new TextEncoder().encode(message); // encode as (utf-8) Uint8Array
		const hashBuffer = await window.crypto.subtle.digest(algo, msgUint8); // hash the message
		const hashArray = Array.from(new Uint8Array(hashBuffer)); // convert buffer to byte array
		const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join(''); // convert bytes to hex string
		return hashHex;
	} catch (err) {
		return null;
	}
}
// Validate the Floor Object, if the floor key and value is valid and available
export const isCountryWiseFloorObjectValid = (gamUprValidatorProps) => {
	const {
		countryWiseFloorObject,
		countryCodesList,
		validGamUprFloorValues,
		gamUprPlatform,
		videoSectionId,
		gamUprOperatingSystem
	} = gamUprValidatorProps;

	if (!videoSectionId) {
		throw new Error(`VideosectionId is missing in Gam Upr Config in the validator`);
	}
	if (!gamUprPlatform) {
		throw new Error(
			`Gam Upr platform is missing in validator inside video section : ${videoSectionId}`
		);
	}
	if (!gamUprOperatingSystem) {
		throw new Error(
			`Gam Upr operating system is missing in the validator for platform: ${gamUprPlatform} inside Gam Upr Config in video section: ${videoSectionId}.`
		);
	}

	if (!countryWiseFloorObject) {
		throw new Error(
			`Country-wise floor object is missing in the validator for platform: ${gamUprPlatform}, operating system: ${gamUprOperatingSystem} inside Gam Upr Config in video section: ${videoSectionId}.`
		);
	}

	if (!countryCodesList) {
		throw new Error(
			`Country codes list is missing in the validator for platform: ${gamUprPlatform}, operating system: ${gamUprOperatingSystem} inside Gam Upr Config in video section: ${videoSectionId}.`
		);
	}

	if (!validGamUprFloorValues) {
		throw new Error(
			`List of available Gam Upr floors is missing in the validator for platform: ${gamUprPlatform}, operating system: ${gamUprOperatingSystem} inside Gam Upr Config in video section: ${videoSectionId}.`
		);
	}

	// It checks if the operatingSystem object has a DEFAULT code, which is mandatory for all operatingSystem present in Gam Upr.
	if (!countryWiseFloorObject.hasOwnProperty('DEFAULT')) {
		throw new Error(
			`DEFAULT code is required for platform: ${gamUprPlatform}, operating system: ${gamUprOperatingSystem} inside Gam Upr Config in video section: ${videoSectionId}.`
		);
	}

	// It checks if the Gam Upr operatingSystem is Default, it validates if the object has exactly one code which should be DEFAULT code.
	if (gamUprPlatform === 'DEFAULT' && Object.keys(countryWiseFloorObject).length !== 1) {
		throw new Error(
			`The DEFAULT platform in Gam Upr can only have the DEFAULT code; no other country codes are allowed for operating system: ${gamUprOperatingSystem} inside Gam Upr Config in video section: ${videoSectionId}.`
		);
	}

	Object.keys(countryWiseFloorObject).forEach((countryCode) => {
		if (!countryCodesList.includes(countryCode)) {
			throw new Error(
				`Invalid country code: ${countryCode}. Please select a valid code for platform: ${gamUprPlatform}, operating system: ${gamUprOperatingSystem} inside Gam Upr Config in video section: ${videoSectionId}.`
			);
		}

		let floorValue = countryWiseFloorObject[countryCode];

		if (typeof floorValue !== 'string' || !validGamUprFloorValues.includes(floorValue)) {
			throw new Error(
				`Invalid or unavailable floor value for country code: ${countryCode} in platform: ${gamUprPlatform}, operating system: ${gamUprOperatingSystem} inside Gam Upr Config in video section: ${videoSectionId}.`
			);
		}
	});
};

/**
 * @name throwErrorIfKeyNotPresentOrInvalid
 * @description This function checks if key is present in the object, also checks the value type\
 * if check fails , it throws error
 * @param {string} key
 * @param {object} obj
 * @param {string} keyValueType
 */
export const throwErrorIfKeyNotPresentOrInvalid = (key, obj, keyValueType) => {
	if (!(key in obj)) {
		throw new Error(`${key} not present!`);
	}
	if (keyValueType && typeof obj[key] !== keyValueType) {
		throw new Error(`typeof ${key} is invalid!`);
	}
};

/**
 * @name hasOwnPropertySafe
 * @description Checks if an object has a specific property as its own (not inherited).
 * @param {Object} obj - The object to inspect.
 * @param {string} prop - The name of the property to check.
 * @returns {boolean} True if the property exists, false otherwise.
 */
export const hasOwnPropertySafe = (obj, prop) => {
	if (obj === null || obj === undefined) {
		return false;
	}
	return Object.prototype.hasOwnProperty.call(obj, prop);
};

/**
 * @name checkType
 * @description Evaluates the type of a given value and checks if it matches the expected data type.
 * @param {*} value - The value whose type is to be checked. It can be any type (e.g., object, string, number, etc.).
 * @param {string} expectedType - A string that specifies the expected data type of the 'value'.
 * This parameter is case-sensitive, expecting : 'array', 'number', 'boolean'.
 * @returns {boolean} Returns 'true' if the 'value' matches the 'expectedType', and 'false' otherwise.
 * If an unsupported 'expectedType' is provided, the function defaults to returning 'false'.
 */
export const checkType = (value, expectedType) => {
	let result = false;

	switch (expectedType) {
		case 'array':
			result = Array.isArray(value);
			break;
		case 'number':
			result = typeof value === 'number';
			break;
		case 'boolean':
			result = typeof value === 'boolean';
			break;
		default:
			result = false;
			break;
	}

	return result;
};

/**
 * @name copyToClipboard
 * @description Copies the provided text to the clipboard and alerts a
 * success message or error if the operation fails.
 * @param {string} text - The text to be copied to the clipboard.
 * @param {string} successMessage - The message to display upon successful copying.
 */

export const copyToClipboard = (text, successMessage) => {
	try {
		navigator.clipboard.writeText(text).then(() => alert(successMessage));
	} catch (err) {
		alert(err);
	}
};

/**
 * @name checkValidURI The function `checkValidURI` checks if a given Vast Tag is a valid URI.
 * @param {string} vastTag - The `vastTag` parameter is a string that represents a VAST tag.
 * @throws {Error} - Throws an error if any validation fails.
 * @returns {Boolean} a boolean value of true if the vastTag is valid and follows the format of a valid URI.
 */
export const checkValidURI = (url) => {
	const uriRegex = /^(ftp|http|https):\/\/[^ "]+$/;
	if (url === '' || url === undefined) {
		throw new Error('Please input vast tag first');
	}
	if (!uriRegex.test(url)) {
		throw new Error('Vast Tag format should be a valid URI');
	}
	return true;
};

/**
 * @name debounce The function `debounce` adds a delay before executing a function, it takes help of setTimeout and clearTimeout to achieve that.
 * @param {function} func - the function to be executed after delay.
 * @param {Number} delay - delay time in milliseconds
 * @returns {function} returns a function that will be executed after delay with the arguments
 */
export function debounce(func, delay) {
	let timeoutId;

	return function (...args) {
		clearTimeout(timeoutId);

		timeoutId = setTimeout(() => {
			func.apply(this, args);
		}, delay);
	};
}

/**
 * @name validateNoWhiteSpaces
 * @description This function checks if a string has any whitespace character.
 * @param {string} str
 */
export function validateNoWhiteSpaces(str) {
	if (/\s/g.test(str)) {
		throw new Error(`${str} should not contain any whitespace character`);
	}
}

/**
 * @name getJsonDiff
 * @description This function takes two JSON objects and returns the difference between them.
 * @param {object} previousConfig - The previous config.
 * @param {object} currConfig - The current config.
 * @returns {object} - The difference between the two JSON objects.
 */
export function getJsonDiff(previousConfig, currConfig) {
	return jsondiffpatch.diff(previousConfig, currConfig) || {};
}

/**
 * @name patchJson
 * @description This function takes a JSON object and a diff object and returns the patched JSON object.
 * @param {object} previousConfig
 * @param {object} diff
 * @returns {object}
 */
export function patchJson(previousConfig, diff) {
	return jsondiffpatch.patch(previousConfig, diff);
}

/**
 * @name prepareUnsyncedAdUnitObj
 * @description Function to prepare the unsynced adUnit object based on the adUnit details.
 * @param {object} adDetailsObj - Object with details about the adUnit such as platform, adUnitType etc.
 * @returns {object} singleUnsyncedAdUnitObj  - The unsynced adUnit object to be sent in payload ads array to syncDfpAdUnits.
 */
export function prepareUnsyncedAdUnitObj(adDetailsObj) {
	const { adUnit, platform, adUnitType, dfpNetworkConfig, videoSectionUnit } = adDetailsObj;
	const gamCode = adUnit.gamCode;
	const isThirdPartyDFP = gamCode !== GAMIDS.AP_GAM_ID; // true if gamCode is different from AP GAM Code
	const singleUnsyncedAdUnitObj = { ...initialUnsyncedDfpAdUnitObj };

	singleUnsyncedAdUnitObj.sectionName = videoSectionUnit.name;
	singleUnsyncedAdUnitObj.adUnitName = adUnit.name;
	singleUnsyncedAdUnitObj.sectionId = videoSectionUnit.videoSectionId;
	singleUnsyncedAdUnitObj.platform = platform;
	singleUnsyncedAdUnitObj.type = adUnitType;
	// If the adUnit is of third party DFP then, we'll add additional properties to singleUnsyncedAdUnitObj.
	if (isThirdPartyDFP) {
		// If the GAM Code is not present in the dfpNetworkConfig, then we'll throw an error.
		if (!dfpNetworkConfig.gamNetworks[gamCode]) {
			throw new Error(
				`Cannot Create Ad Units with Unsupported GAM Code: ${gamCode} in ${platform}/${adUnitType}!`
			);
		}
		singleUnsyncedAdUnitObj.activeDFPNetwork = gamCode;
		singleUnsyncedAdUnitObj.activeDFPParentId = dfpNetworkConfig.gamNetworks[gamCode].parentId;
		singleUnsyncedAdUnitObj.isThirdPartyDFP = isThirdPartyDFP;
		singleUnsyncedAdUnitObj.refreshToken = dfpNetworkConfig.gamNetworks[gamCode].refreshToken;
	}

	return singleUnsyncedAdUnitObj;
}

/**
 * @name getUnsyncedAdUnits
 * @description Function to get the unsynced dfpAdUnits based on the condition
 * if the adUnitCode property of Ad Units is an empty string.
 * @param {Object} siteConfig - The Site Config Object
 * @returns {Array} unsyncedAdUnitsArray - The array of unsynced adUnitsObj
 */
export async function getUnsyncedAdUnits(siteConfig) {
	const unsyncedAdUnitsArray = [];
	const dfpNetworkConfigResponse = await getDfpNetworkConfig();
	const dfpNetworkConfig = dfpNetworkConfigResponse?.data?.data?.dfpNetworkConfig;
	const units = siteConfig.ads;

	for (const videoSectionUnit of units) {
		const dfpAdUnitsObj = videoSectionUnit.networkData.dfpAdUnits;
		for (const [platform, platformAdUnitObj] of Object.entries(dfpAdUnitsObj)) {
			for (const [adUnitType, adUnitsArray] of Object.entries(platformAdUnitObj)) {
				for (const adUnit of adUnitsArray) {
					if (!adUnit.adUnitCode) {
						const adDetailsObj = {
							adUnit,
							platform,
							adUnitType,
							dfpNetworkConfig,
							videoSectionUnit
						};
						const singleUnsyncedAdUnitObj = prepareUnsyncedAdUnitObj(adDetailsObj);
						unsyncedAdUnitsArray.push(singleUnsyncedAdUnitObj);
					}
				}
			}
		}
	}
	return unsyncedAdUnitsArray;
}

/**
 * @name convertTimeStamp
 * @description Function to convert a timestamp into a date string.
 * @param {number} timestamp - The timestamp to convert.
 * @returns {object} - The formatted date and time.
 */
export function convertTimeStamp(timestamp) {
	const dateTime = new Date(timestamp).toISOString().replace('T', ' ').split('.')[0];
	const date = dateTime.split(' ')[0];
	const time = dateTime.split(' ')[1].slice(0, -3);
	return { date, time };
}

export const getStatusColor = (processStatus) =>
	({
		INITIALISED: 'warning',
		PROCESSED: 'success',
		FAILED: 'error'
	})[processStatus];

/**
 * @name validatePlayerSizes
 * @description Validates the player sizes for each device, ensuring they meet minimum height and width requirements.
 *
 * @param {object} playerSizesConfig - Object containing player size configurations for different devices.
 * @param {string} playerType - The type of player being validated.
 * @param {string} videoSectionId - The video section id .
 * @throws {Error} If the height or width of any device is less than the minimum required values.
 */
export const validatePlayerSizes = (playerSizesConfig, playerType, videoSectionId) => {
	for (const device in playerSizesConfig) {
		if (playerSizesConfig[device].height < VIDEO_PLAYER_SIZES.MIN_HEIGHT) {
			throw new Error(
				`Height of ${device} in ${playerType} is less than ${VIDEO_PLAYER_SIZES.MIN_HEIGHT} in video section ${videoSectionId}`
			);
		}
		if (playerSizesConfig[device].width < VIDEO_PLAYER_SIZES.MIN_WIDTH) {
			throw new Error(
				`Width of ${device} in ${playerType} is less than ${VIDEO_PLAYER_SIZES.MIN_WIDTH} in video section ${videoSectionId}`
			);
		}
	}
};

/**
 * Checks if the ad section has a valid fallback config (scriptType and tagContent).
 *
 * @param {Object} adSection - The ad section to validate.
 * @returns {boolean} - True if valid, false otherwise.
 */
export function hasValidFallbackConfig(adSection) {
	const isFallBackTagExist = !!(adSection[TAG_TYPE] && adSection[TAG_CONTENT]);
	const ishtmlOrScript = adSection[TAG_TYPE] === SCRIPT || adSection[TAG_TYPE] === HTML;
	return isFallBackTagExist && ishtmlOrScript;
}

/**
 * Decodes fallback tagContent if the config is valid.
 *
 * @param {Object} adSection - The ad section to decode.
 * @returns {Object} - Cloned ad section with decoded content.
 */
export function preProcessConfigAfterFlatten(adSection) {
	const clonedAdSection = structuredClone(adSection);
	const hasValidFallback = hasValidFallbackConfig(adSection);
	if (hasValidFallback) {
		clonedAdSection[TAG_CONTENT] = window.atob(clonedAdSection[TAG_CONTENT]);
	}
	return clonedAdSection;
}

/**
 * Encodes fallback tagContent if the config is valid.
 *
 * @param {Object} adSection - The ad section to encode.
 * @returns {Object} - Cloned ad section with encoded content.
 */
export function preProcessConfigBeforeDeFlatten(adSection) {
	const clonedAdSection = structuredClone(adSection);
	const hasValidFallback = hasValidFallbackConfig(adSection);
	if (hasValidFallback) {
		clonedAdSection[TAG_CONTENT] = window.btoa(clonedAdSection[TAG_CONTENT]);
	}
	return clonedAdSection;
}

/**
 * Validates the operating system configuration for a given platform.
 *
 * @param {Object} osValidationParams - Parameters required for validation.
 * @throws {Error} If country-wise floor mapping is missing.
 */
export function validateOperatingSystem(osValidationParams) {
	const {
		platformObject,
		gamUprPlatform,
		gamUprOperatingSystem,
		countryCodesList,
		validGamUprFloorValues,
		videoSection
	} = osValidationParams;

	const operatingSystemObject = platformObject?.[gamUprOperatingSystem];
	if (!operatingSystemObject) {
		return;
	}

	if (!operatingSystemObject?.countryWiseFloorMapping) {
		throw new Error(
			`Country-wise floor mapping is missing in Gam Upr Config for platform: ${gamUprPlatform}, operating system: ${gamUprOperatingSystem} in video section: ${videoSection.videoSectionId}`
		);
	}

	const gamUprValidatorData = {
		countryWiseFloorObject: operatingSystemObject.countryWiseFloorMapping,
		countryCodesList,
		validGamUprFloorValues,
		gamUprPlatform,
		videoSectionId: videoSection.videoSectionId,
		gamUprOperatingSystem
	};

	isCountryWiseFloorObjectValid(gamUprValidatorData);
}

/**
 * @name getBaseVastUri
 * @description Generates the Base Vast Tag URI
 * @param {Boolean} liveTrafficEnabled - Boolean indicating if live traffic is enabled.
 * @returns {String} Base Vast Tag URI string.
 */
const getBaseVastUri = (liveTrafficEnabled) => {
	return liveTrafficEnabled
		? VAST_TAG_CONSTANTS.BASE_URL.replace(/\/ads$/, '/live/ads')
		: VAST_TAG_CONSTANTS.BASE_URL;
};

/**
 * @name getBaseVastTagString
 * @description Generates the Base Vast Tag String
 * @param {Object} siteConfig - The site configuration object.
 * @param {Object} adUnitAndVastDetails - The ad unit and VAST details object.
 * @returns {String} baseVastTagString - Base Vast Tag String.
 */
const getBaseVastTagString = (siteConfig, adUnitAndVastDetails) => {
	const baseVastUri = getBaseVastUri(adUnitAndVastDetails.enableForLiveTraffic);
	const gamCode = adUnitAndVastDetails?.gamCode;
	const adUnitName = adUnitAndVastDetails?.adUnitName;

	const adUnitValue = adUnitAndVastDetails.enableChildNetwork
		? `/${gamCode},${siteConfig?.mcmId}/${adUnitName}`
		: `/${gamCode}/${adUnitName}`;

	const baseVastTagString = `${baseVastUri}${QUESTION_MARK}${KEYS.IU}${EQUAL}${adUnitValue}${AMPERSAND}`;

	return baseVastTagString;
};

/**
 * @name buildParamsMap
 * @description Build the Parans map with Vast Tag Key-Value Pairs.
 * @param {Object} adUnitAndVastDetails - The ad unit and VAST details object.
 * @returns {Map} params - Params Map of Vast Tag KVP.
 */
const buildParamsMap = (adUnitAndVastDetails) => {
	const params = new Map();

	params.set(KEYS.ENV, VALUES.VP);
	params.set(KEYS.DESCRIPTION_URL, encodeURIComponent(adUnitAndVastDetails.descriptionUrl));
	params.set(KEYS.SZ, adUnitAndVastDetails.sizes.join(ENCODED_PIPE));
	params.set(
		KEYS.CUSTOM_PARAMS,
		adUnitAndVastDetails.customParams.replace(/=/g, ENCODED_EQUAL).replace(/&/g, ENCODED_AMPERSAND)
	);
	params.set(KEYS.TFCD, VALUES.TFCD.ZERO);
	params.set(KEYS.NPA, VALUES.NPA.ZERO);
	params.set(KEYS.GDFP_REQ, VALUES.GDFP_REQ.ONE);
	params.set(KEYS.CORRELATOR, adUnitAndVastDetails.timestamp || Date.now().toString());
	params.set(KEYS.UNVIEWED_POSITION_START, VALUES.UNVIEWED_POSITION_START.ONE);
	params.set(KEYS.IMPL, VALUES.IMPL.S);
	params.set(KEYS.OUTPUT, VALUES.OUTPUT_VAST);
	params.set(KEYS.VAD_TYPE, adUnitAndVastDetails.videoFormatType.toLowerCase().replace('-', ''));
	params.set(KEYS.MAX_AD_DURATION, adUnitAndVastDetails.adDuration);
	params.set(KEYS.NOFB, VALUES.NOFB.ONE);

	// Setting Additional Parameters which are not included in initialAdUnitAndVastDetailsObj.
	Object.entries(adUnitAndVastDetails).forEach(([key, value]) => {
		if (!Object.keys(initialAdUnitAndVastDetailsObj).includes(key) && key !== 'timestamp') {
			params.set(key, value);
		}
	});

	return params;
};

/**
 * @name getVastTagUrl
 * @description Generates the VAST tag URL based on the site configuration and ad unit details.
 * @param {object} siteConfig - The site configuration object.
 * @param {object} adUnitAndVastDetails - The ad unit and VAST details object.
 * @returns {string} - The generated VAST tag URL.
 */
export const getVastTagUrl = (siteConfig, adUnitAndVastDetails) => {
	const baseVastTagString = getBaseVastTagString(siteConfig, adUnitAndVastDetails);
	const params = buildParamsMap(adUnitAndVastDetails);

	const dynamicVastTagString = Array.from(params)
		.map(([key, value]) => `${key}${EQUAL}${value}`)
		.join(AMPERSAND);

	const finalVastTagString = `${baseVastTagString}${dynamicVastTagString}`;
	return finalVastTagString;
};

/**
 * @name validateVastTagDetails
 * @description Checks if any values in the `adUnitAndVastDetailsObj` object are empty strings
 * or empty arrays and throws an error if so.
 * @param {object} adUnitAndVastDetailsObj - Object having ad unit and vast details.
 * @throws {Error} - Throws an error if validation fails.
 */
export const validateVastTagDetails = (adUnitAndVastDetailsObj) => {
	for (const [key, value] of Object.entries(adUnitAndVastDetailsObj)) {
		if (
			(typeof value === 'string' && value === '') ||
			(typeof value === 'object' && value.length === 0)
		) {
			throw new Error(`${key} value missing!`);
		}
	}
};

/**
 * @name validateAdUnitSyncingData
 * @description Validates the ad unit syncing data.
 * @param {object} adUnitAndVastDetailsObj - The Unsynced Ad and Vast Detail Object.
 * @throws {Error} Will throw an error if ad unit name, section ID, or GAM code is missing.
 */
export const validateAdUnitSyncingData = (adUnitAndVastDetailsObj) => {
	const missingFields = [];
	const { adUnitName, sectionId, gamCode } = adUnitAndVastDetailsObj;

	if (!adUnitName) {
		missingFields.push('Ad Unit Name');
	}
	if (!sectionId) {
		missingFields.push('Section ID');
	}
	if (!gamCode) {
		missingFields.push('GAM Code');
	}

	if (missingFields.length > 0) {
		throw new Error(
			`Please select the following fields to sync ad units: ${missingFields.join(', ')}.`
		);
	}
};
/**
 * Truncates a string to a specified length and appends '...' if it exceeds the length.
 *
 * @param {string} text - The text to be truncated.
 * @param {number} [textLength=10] - The maximum length of the text. Defaults to 10 if not provided.
 * @returns {string} The truncated text, with '...' appended if it exceeds the specified length.
 */
export const truncateText = (text, textLength = 100) => {
	return text.length > textLength ? text.slice(0, textLength) + '...' : text;
};

/**
 * Removes protocol and leading www from a site domain or URL.
 *
 * @param {string} siteDomain - Domain or URL value.
 * @returns {string} Normalized display value.
 */
export const trimSiteDomain = (siteDomain = '') => {
	return String(siteDomain).trim().replace(/^(?:https?:\/\/)?(?:www\.)?/i, '').replace(/\/$/, '');
};

/**
 * Retrieves a percentage value from the configuration and ensures it does not exceed 100.
 *
 * @param {Object} rawConfig - The configuration object containing the values.
 * @param {string} key - The key to access the specific percentage value in the configuration.
 * @param {number} defaultValue - The fallback value to use .
 * @returns {number} A number between 0 and 100.
 */
export const getPercentageValue = (rawConfig, key, defaultValue) => {
	const value = rawConfig[key];
	return typeof value === 'number' ? Math.min(value, 100) : defaultValue;
};

/**
 * Validates that a given value is a number between 0 and 100 (inclusive).
 *
 * @param {number} value - The value to validate.
 * @param {string} key - The key name to include in the error message for context.
 * @param {string|number} videoSectionId - The ID of the video section for detailed error context.
 * @throws Will throw an error if the value is not a number or is outside the 0-100 range.
 */
export const validatePercentage = ({ value, key, feature, videoSectionId }) => {
	if (!Number.isFinite(value) || value < 0 || value > 100) {
		throw new Error(
			`'${key}' must be a number between 0 and 100 (inclusive) in '${feature}' for video section ${videoSectionId}.`
		);
	}
};
/**
 * @name isValidEmail
 * @description Validates if the given string is a valid email address.
 * @param {string} email - The email string to validate.
 * @returns {boolean} - Returns true if the email is valid, otherwise false.
 */
export function isValidEmail(email) {
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	return emailRegex.test(email);
}

/**
 * @name handleFramerateSiteDocUpdation
 * @description Handles site creation/update with proper validation and state updates
 * @param {Object} params Configuration object containing all necessary parameters
 * @returns {Promise<Object>} The response from the server
 */
export const handleFramerateSiteDocUpdation = async ({
	mode,
	overviewObject,
	previousCouchBaseConfig,
	allValidSiteTags,
	dfpNetworkConfig,
	initializeConfigData = null,
	curSelectedUnit = null,
	setPreviousCouchBaseConfig = null,
	shouldValidateChanges = true
}) => {
	try {
		// If in UPDATE mode and validation is required, check for changes
		if (mode === 'UPDATE' && shouldValidateChanges) {
			const siteDocChangesMade =
				Object.keys(getJsonDiff(previousCouchBaseConfig, overviewObject)).length > 0;
			if (!siteDocChangesMade) {
				throw new Error('Please make changes to site doc for Update!');
			}
		}

		let response;
		let params = {
			data: overviewObject,
			dfpNetworkConfig,
			allValidSiteTags,
			previousCouchBaseConfig,
			mode
		};
		if (mode === 'UPDATE') {
			response = await updateSite(params, overviewObject.siteId);
			const latestSiteConfigFromDb = response?.data?.data?.site;

			if (initializeConfigData && curSelectedUnit) {
				initializeConfigData(latestSiteConfigFromDb, curSelectedUnit);
			}
			if (setPreviousCouchBaseConfig) {
				setPreviousCouchBaseConfig(latestSiteConfigFromDb);
			}
		} else if (mode === 'CREATE') {
			params = { ...params, previousCouchBaseConfig: {} };
			response = await createSite(params);
		}

		return response?.data;
	} catch (err) {
		const errorMessage = err?.response?.data?.data?.message || err?.message;
		throw new Error(errorMessage);
	}
};

/**
 * Format a date according to the given format string,
 * a rip-off of `date-fns` library
 *
 * Takes a JavaScript Date object and converts it to a readable string
 * using format tokens. Each token in the format string gets replaced with
 * the corresponding part of the date.
 *
 * @param {Date} date - The JavaScript Date object to format
 * @param {string} formatStr - Format string with tokens to be replaced
 *
 * @returns {string} The formatted date string
 *
 * @example
 * // Basic date formatting
 * const date = new Date('2025-06-29T20:43:15');
 *
 * format(date, 'MMM dd, yyyy')           // "Jun 29, 2025"
 * format(date, 'hh:mm a')                // "08:43 PM"
 * format(date, 'MMM dd, yyyy hh:mm a')   // "Jun 29, 2025 08:43 PM"
 *
 * @example
 * // Different date formats
 * format(date, 'EEEE, MMMM dd, yyyy')   // "Sunday, June 29, 2025"
 * format(date, 'M/d/yy')                 // "6/29/25"
 * format(date, 'yyyy-MM-dd')             // "2025-06-29"
 * format(date, 'HH:mm:ss')               // "20:43:15" (24-hour)
 * format(date, 'h:mm:ss a')              // "8:43:15 PM" (12-hour)
 *
 * @throws {Error} Throws an error if date is not a valid Date object
 */
export function format(date, formatStr) {
	if (!(date instanceof Date) || isNaN(date)) {
		throw new Error('Invalid date provided');
	}

	const tokens = {
		// Year
		yyyy: () => date.getFullYear().toString(),
		yy: () => date.getFullYear().toString().slice(-2),

		// Month
		MMMM: () => date.toLocaleString('en-US', { month: 'long' }),
		MMM: () => date.toLocaleString('en-US', { month: 'short' }),
		MM: () => String(date.getMonth() + 1).padStart(2, '0'),
		M: () => String(date.getMonth() + 1),

		// Day
		dd: () => String(date.getDate()).padStart(2, '0'),
		d: () => String(date.getDate()),

		// Day of week
		EEEE: () => date.toLocaleString('en-US', { weekday: 'long' }),
		EEE: () => date.toLocaleString('en-US', { weekday: 'short' }),

		// Hour (24-hour)
		HH: () => String(date.getHours()).padStart(2, '0'),
		H: () => String(date.getHours()),

		// Hour (12-hour)
		hh: () => {
			const hour = date.getHours() % 12 || 12;
			return String(hour).padStart(2, '0');
		},
		h: () => {
			const hour = date.getHours() % 12 || 12;
			return String(hour);
		},

		// Minutes
		mm: () => String(date.getMinutes()).padStart(2, '0'),
		m: () => String(date.getMinutes()),

		// Seconds
		ss: () => String(date.getSeconds()).padStart(2, '0'),
		s: () => String(date.getSeconds()),

		// AM/PM
		a: () => (date.getHours() >= 12 ? 'PM' : 'AM'),
		aa: () => (date.getHours() >= 12 ? 'PM' : 'AM'),
		A: () => (date.getHours() >= 12 ? 'PM' : 'AM')
	};

	// Sort tokens by length (longest first) to avoid partial replacements
	const sortedTokens = Object.keys(tokens).sort((a, b) => b.length - a.length);
	const tokenRegex = new RegExp(sortedTokens.join('|'), 'g');

	return formatStr.replace(tokenRegex, (token) => tokens[token]());
}

export const sortAdUnitsByGam = (adUnits) => {
	const gamOrder = [];
	const grouped = {};
	for (const unit of adUnits) {
		if (!grouped[unit.gamCode]) {
			grouped[unit.gamCode] = [];
			gamOrder.push(unit.gamCode);
		}
		grouped[unit.gamCode].push(unit);
	}
	const sorted = [];
	const maxLen = Math.max(...gamOrder.map((g) => grouped[g].length));
	for (let i = 0; i < maxLen; i++) {
		for (const gam of gamOrder) {
			if (grouped[gam][i] !== undefined) {
				sorted.push(grouped[gam][i]);
			}
		}
	}
	return sorted;
};

export const allGamsHaveEqualUnits = (adUnits) => {
	if (!adUnits || adUnits.length === 0) return true;
	const counts = {};
	for (const unit of adUnits) {
		counts[unit.gamCode] = (counts[unit.gamCode] || 0) + 1;
	}
	const values = Object.values(counts);
	return values.every((v) => v === values[0]);
};

/**
 * Returns all non-empty ad unit arrays across every platform × adUnitType combination.
 * @param {Object} rawConfig
 * @param {string} keyData
 * @param {string[]} platforms - e.g. ['DESKTOP', 'MOBILE', 'TABLET']
 * @param {string[]} adUnitTypeKeys - e.g. ['defaultUnits', 'backfillUnits', 'noMcmUnits']
 * @returns {{ key: string, arr: object[] }[]}
 */
export const getAllAdUnitArrays = (rawConfig, keyData, platforms, adUnitTypeKeys) => {
	const allAdUnits = [];
	for (const platform of platforms) {
		for (const adUnitType of adUnitTypeKeys) {
			const key = `${keyData}.${platform}.${adUnitType}`;
			const arr = rawConfig[key];
			if (arr && arr.length > 0) {
				allAdUnits.push({ key, arr });
			}
		}
	}
	return allAdUnits;
};
